import RedeemRoundedIcon from '@mui/icons-material/RedeemRounded';
import { Chip, alpha, styled } from '@mui/material';
import Tooltip, { tooltipClasses, type TooltipProps } from '@mui/material/Tooltip';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import type RewardStore from '../../AppData/_stores/RewardStore';

interface ProductCatalogRewardBadgeProps {
  reward?: RewardStore | null
  focusable?: boolean
}

const RewardBadge = styled(Chip)(({ theme }) => ({
  fontWeight: 800,
  flexShrink: 0,
  transition: 'background-color 160ms ease, border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease',

  '& .MuiChip-label': {
    paddingLeft: theme.spacing(0.75),
    paddingRight: theme.spacing(0.75),
  },

  '&:hover, &:focus-visible': {
    backgroundColor: '#ffef99',
    borderColor: alpha('#b58a00', 0.64),
    boxShadow: '0 4px 10px ' + alpha('#d4a300', 0.22),
    outline: 'none',
    transform: 'translateY(-1px)',
  },

  '& .MuiChip-icon': {
    color: '#b58a00',
  },

  [theme.breakpoints.down('sm')]: {
    fontSize: 11,
    height: 21,

    '& .MuiChip-label': {
      paddingLeft: theme.spacing(0.625),
      paddingRight: theme.spacing(0.625),
    },

    '& .MuiChip-icon': {
      fontSize: 14,
      marginLeft: theme.spacing(0.625),
    },
  },
}));

const RewardTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    border: '1px solid ' + alpha('#d4a300', 0.32),
    borderRadius: 8,
    boxShadow: '0 14px 34px ' + alpha('#332500', 0.18),
    color: theme.palette.text.primary,
    maxWidth: 270,
    minWidth: 230,
    padding: theme.spacing(1.5),
  },

  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.common.white,

    '&::before': {
      border: '1px solid ' + alpha('#d4a300', 0.32),
      boxSizing: 'border-box',
    },
  },

  [theme.breakpoints.down('sm')]: {
    [`& .${tooltipClasses.tooltip}`]: {
      maxWidth: 240,
      minWidth: 210,
      padding: theme.spacing(1.25),
    },
  },
}));

const RewardTooltipContent = styled('span')(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(0.75),
}));

const RewardTooltipTitle = styled('span')(({ theme }) => ({
  alignItems: 'center',
  color: '#806100',
  display: 'inline-flex',
  fontSize: 12,
  fontWeight: 800,
  gap: theme.spacing(0.5),
  letterSpacing: 0,
  lineHeight: 1.2,
  textTransform: 'uppercase',

  '& .MuiSvgIcon-root': {
    color: '#b58a00',
    fontSize: 16,
  },
}));

const RewardTooltipOffer = styled('span')({
  color: '#2e2500',
  display: 'block',
  fontSize: 22,
  fontWeight: 800,
  letterSpacing: 0,
  lineHeight: 1.05,
});

const RewardTooltipBody = styled('span')(({ theme }) => ({
  color: theme.palette.text.secondary,
  display: 'block',
  fontSize: 13,
  fontWeight: 500,
  letterSpacing: 0,
  lineHeight: 1.35,
}));

function ProductCatalogRewardBadge(props: ProductCatalogRewardBadgeProps) {
  const [t] = useTranslation('products');
  const rewardValue = props.reward?.get('value');
  const rewardPercent = props.reward?.get('type') === 'percent' && typeof rewardValue === 'number' ? rewardValue : undefined;

  if (rewardPercent === undefined) {
    return null;
  }

  const tooltipTitle = <RewardTooltipContent>
    <RewardTooltipTitle>
      <RedeemRoundedIcon aria-hidden="true" fontSize="inherit" />
      {t('Sample reward')}
    </RewardTooltipTitle>
    <RewardTooltipOffer>
      {t('{{percent}}% off your first one', { percent: rewardPercent })}
    </RewardTooltipOffer>
    <RewardTooltipBody>
      {t('Design this product to unlock the discount before you order.')}
    </RewardTooltipBody>
  </RewardTooltipContent>;

  return <RewardTooltip
    arrow
    enterTouchDelay={0}
    leaveTouchDelay={3000}
    placement="top"
    title={tooltipTitle}
  >
    <RewardBadge
      aria-label={t('{{percent}}% first sample reward available', { percent: rewardPercent })}
      color="reward"
      icon={<RedeemRoundedIcon aria-hidden="true" fontSize="inherit" />}
      label={`${rewardPercent}%`}
      tabIndex={props.focusable === false ? undefined : 0}
      variant="badge"
    />
  </RewardTooltip>;
}

export default memo(ProductCatalogRewardBadge);
